import React, { Component, ReactNode } from 'react';
import { Modal, Button, Typography, Space, message } from 'antd';
import { ExclamationCircleOutlined, CopyOutlined, RedoOutlined } from '@ant-design/icons';


const { Text, Paragraph } = Typography;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  modalVisible: boolean;
}

class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      modalVisible: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      modalVisible: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Optional: Log error to external service
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleCopyError = async () => {
    const { error, errorInfo } = this.state;

    const errorDetails = {
      message: error?.message || 'Unknown error',
      stack: error?.stack || 'No stack trace available',
      componentStack:
        errorInfo?.componentStack || 'No component stack available',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    const errorText = `Error Details:
${JSON.stringify(errorDetails, null, 2)}`;

    try {
      await navigator.clipboard
        .writeText(errorText)
        .then(() => message.success('Error copied to clipboard'))
        .catch((e) =>
          message.warning('Error copied to clipboard (fallback method)')
        );
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = errorText;
      document.body.appendChild(textArea);
      textArea.select();
      // Modern fallback
      await navigator.clipboard.writeText(errorText).catch(() => {
        message.warning('Failed to copy error details');
      });
      document.body.removeChild(textArea);
      message.warning('Error copied to clipboard (fallback method)');
    }
  };

  render() {
    const { hasError, modalVisible, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div className="error-boundary">
          {/* Render a minimal fallback UI */}
          <div
            style={{
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <Text type="secondary">Something went wrong...</Text>
          </div>

          {/* Error Modal */}
          <Modal
            title={
              <Space>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                Error Occurred
              </Space>
            }
            open={modalVisible}
            closable={false}
            // onCancel={() => this.setState({ modalVisible: false })}
            footer={[
              <Button
                key="copy"
                icon={<CopyOutlined />}
                onClick={this.handleCopyError}
                style={{ borderRadius: '16px' }}
              >
                Copy Error
              </Button>,
              <Button
                key="reload"
                type="primary"
                icon={<RedoOutlined />}
                onClick={this.handleReload}
                style={{ borderRadius: '16px' }}
              >
                Reload Page
              </Button>,
            ]}
            width={500}
            centered
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                An unexpected error has occurred. You can copy the error details
                and reload the page to try again.
              </Paragraph>

              {error?.message && (
                <div>
                  <Text strong>Error Message:</Text>
                  <Paragraph
                    code
                    copyable={false}
                    style={{
                      backgroundColor: '#fff2f0',
                      padding: '8px',
                      marginTop: '4px',
                      borderRadius: '4px',
                      wordBreak: 'break-word',
                      maxHeight: '200px',
                    }}
                  >
                    {error.message}
                  </Paragraph>
                </div>
              )}
            </Space>
          </Modal>
        </div>
      );
    }

    return children;
  }
}

export default AppErrorBoundary;
